import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import { getWindow } from '@surfline/web-common';
import { useWindowSize } from 'react-use';
import classNames from 'classnames';
import { Box, Button, IconButton, Typography, Skeleton } from '@mui/material';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import { formatInTimeZone } from 'date-fns-tz';

import {
  SL_WEB_CAMERA_USER_FEEDBACK_MODAL,
  SL_WEB_CAM_PAYWALL_REDESIGN,
  SL_WEB_LIVE_CAM_REWIND_SHARING,
  SL_WEB_FREEMIUM_CAM_HIGHLIGHTS,
  SL_WEB_LINEUP_CAM_PAYWALL,
} from 'common/treatments';
import CamPlayerRewindsScrubber from 'components/CamPlayerRewindsScrubber';
import CamPlayerV2 from 'components/CamPlayerV2';
import { AutoplayOptions, PlayerSkins } from 'components/CamPlayerV2/constants';
import PremiumImageOverlayCTA from 'components/PremiumImageOverlayCTA';
import { Camera } from 'types/camera';
import CamError from 'components/CamError';
import RewindCamPlayer from 'components/RewindCamPlayer';
import CamPlayerPreviewImage from 'components/CamPlayerPreviewImage/CamPlayerPreviewImage';
import FullscreenControl from 'components/FullscreenControl';
import VideoJsAdScripts from 'components/VideoJsAdScripts';
import PlayButtonOverlay from 'components/PlayButtonOverlay';
import { RewindsContext } from 'contexts/RewindsContext';
import { RewindsCamPlayerWithScrubberControlContext } from 'contexts/RewindsCamPlayerWithScrubberControlContext';
import useCheckCameraSupportsSmartRewinds from 'hooks/useCheckCameraSupportsSmartRewinds';
import useIsActivePage from 'hooks/useIsActivePage';
import { getSpotDetails, getSpotReportAssociated } from 'selectors/spot';
import { useUserPermissionStatus, useUserEntitlementStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import { SpotDetails } from 'types/spot';
import getCamStillImage from 'utils/getCamStillImage';
import { useTreatments, isTreatmentWithConfig } from 'utils/treatments';
import useClientSize from 'hooks/useClientSize';
import useStateFromRefState from 'hooks/useStateFromRefState';
import { useMinWidthTablet } from 'hooks/useMediaQueries';
import useStills from 'components/RewindCamPlayer/useStills/useStills';
import CamHostAd from 'components/CamHostAd/CamHostAd';
import useIsTouchDevice from 'hooks/useIsTouchDevice';
import { CamPlayerProps } from 'components/CamPlayerV2/CamPlayerV2';
import LineupCamOverlay, { computeFocusRect } from 'components/LineupCamOverlay';
import LineupCamPaywall from 'components/LineupCamPaywall';
import LiveCamPaywall from 'components/LiveCamPaywall';
import { getDayString } from 'utils/getDayString';
import CamAdPlayer from 'components/CamAdPlayer';
import YesterdayPill from 'components/YesterdayPill';
import PreviewCountdownPill from 'components/PreviewCountdownPill';
import ShareModal from 'components/ShareModal/ShareModal';
import usePlayPauseControls from 'hooks/usePlayPauseControls';
import { useTrackCamPlayback } from 'components/CamPlayerV2/useTrackCamPlayback';
import CamHighlightsPlayer from 'containers/CamHighlightsPlayer';
import { useSpotIsFavorite } from 'hooks/useSpotIsFavorite';
import useClipsButton from 'hooks/useClipsButton';
import { useLineupCamLog } from 'hooks/useLineupCamLog';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';
import { useLineupCamPreview } from './useLineupCamPreview/useLineupCamPreview';
import { useKbygRewindsPlayerAds } from './useKbygRewindsPlayerAds/useKbygRewindsPlayerAds';
import PlaybackSpeedControl from './CamRewindsControlBar/PlaybackSpeedControl/PlaybackSpeedControl';
import CamRewindsControlBar from './CamRewindsControlBar/CamRewindsControlBar';
import LoadingSpinner from './LoadingSpinner/LoadingSpinner';
import CamFeedbackModal from '../CamFeedbackModal';

import { CamPlayerKbygRewindsMultiCam } from './CamPlayerKbygRewindsMultiCam';
import { Player, usePlayerState } from './usePlayerState/usePlayerState';
import SmartClipsOverlayPrompt from './SmartClipsOverlayPrompt/SmartClipsOverlayPrompt';

import styles from './CamPlayerKbygRewinds.module.scss';
import { premiumCamPaywallCard, rewindPaywallCard } from './PaywallCards';
import useKbygRewindsFullscreen from './useKbygRewindsFullscreen/useKbygRewindsFullscreen';
import { LiveCamPlayerHandle } from './CamPlayerKbygRewindsTypes';

export interface CamPlayerKbygRewindsProps {
  camera: Camera;
  enabledFeatures?: {
    ads: {
      page: boolean;
      video: boolean;
    };
    paywalls: boolean;
    wavePool: boolean;
  };
  isMultiCam: boolean;
  multiCamViewActive: boolean;
  numCamsDisplayed: number;
  setActiveMenu: React.Dispatch<React.SetStateAction<'NEARBY' | 'ANGLES'>>;
  setMultiCamViewActive: Dispatch<SetStateAction<boolean>>;
  sortedCameras: Camera[];
}

const premiumOverlayCTACssModules = {
  ctaContainer: styles.ctaContainer,
};

export const CamPlayerKbygRewinds: React.FC<CamPlayerKbygRewindsProps> = ({
  camera,
  enabledFeatures = { ads: { page: true, video: true }, paywalls: true, wavePool: false },
  isMultiCam,
  multiCamViewActive,
  numCamsDisplayed,
  setActiveMenu,
  setMultiCamViewActive,
  sortedCameras,
}) => {
  const router = useRouter();
  const liveCamPlayerHandle = useRef<LiveCamPlayerHandle>(null);
  const playerEventTriggerRef = useRef<HTMLDivElement | null>(null);
  const camPlayerKbygRewindsElement = useRef<HTMLDivElement | null>(null);
  const pageIsActive = useIsActivePage();
  const isNative = router.query.native === 'true';

  const window = getWindow();

  // Start Window Size Section

  const { height, width } = useWindowSize();
  const { clientWidth: playerWidth, isResizing } = useClientSize(
    camPlayerKbygRewindsElement.current,
    {
      observeWidth: true,
      observeHeight: false,
    },
  );
  const isMinWidthTablet = useMinWidthTablet();
  const isTouchDevice = useIsTouchDevice();

  // End Window Size Section

  // Start User Permissions Section

  const {
    hasGraphRewindPermissions,
    hasCamRewindPermissions,
    hasAdFreeCamPermissions,
    hasPremiumCamPermissions,
    hasAdSupportedCamPermissions,
    hasLineupCamPermissions,
  } = useUserPermissionStatus();

  const isUserEntitled = useUserEntitlementStatus();

  const treatments = useTreatments();
  const isFeedbackModalEnabled = treatments[SL_WEB_CAMERA_USER_FEEDBACK_MODAL] === 'on';
  const isCamPaywallRedesignEnabled = treatments?.[SL_WEB_CAM_PAYWALL_REDESIGN] === 'on';
  const isWebSharingEnabled = treatments?.[SL_WEB_LIVE_CAM_REWIND_SHARING] === 'on';
  const camHighlightsTreatment = treatments?.[SL_WEB_FREEMIUM_CAM_HIGHLIGHTS];
  const isCamHighlightsEnabled =
    camHighlightsTreatment === 'on' || camHighlightsTreatment === 'preroll';
  const rawLineupCamTreatment = treatments?.[SL_WEB_LINEUP_CAM_PAYWALL];
  const lineupCamQueryOverride = router.query[SL_WEB_LINEUP_CAM_PAYWALL] as string | undefined;
  const lineupCamPaywallTreatment =
    lineupCamQueryOverride ||
    (isTreatmentWithConfig(rawLineupCamTreatment)
      ? rawLineupCamTreatment.treatment
      : rawLineupCamTreatment);
  const isLineupCamPaywallEnabled = lineupCamPaywallTreatment !== 'off';
  const isLineupCamPreviewEnabled =
    lineupCamPaywallTreatment === 'on_with_preview' ||
    lineupCamPaywallTreatment === 'on_with_overlay_preview';
  const isLineupCamOverlayEnabled = lineupCamPaywallTreatment === 'on_with_overlay_preview';

  let previewDurationSeconds = 10;
  let expandDurationMs = 800;
  let overlayHoldMs = 2500;
  if (isTreatmentWithConfig(rawLineupCamTreatment) && rawLineupCamTreatment.config) {
    try {
      const parsed = JSON.parse(rawLineupCamTreatment.config);
      const parsedDuration = Number(parsed.previewDurationSeconds);
      if (!Number.isNaN(parsedDuration) && parsedDuration > 0) {
        previewDurationSeconds = parsedDuration;
      }
      const parsedExpand = Number(parsed.expandDurationMs);
      if (!Number.isNaN(parsedExpand) && parsedExpand > 0) {
        expandDurationMs = parsedExpand;
      }
      const parsedHold = Number(parsed.overlayHoldMs);
      if (!Number.isNaN(parsedHold) && parsedHold > 0) {
        overlayHoldMs = parsedHold;
      }
    } catch {
      /* fallback to default */
    }
  }

  const [livePlaying, setLivePlaying] = useState<boolean>(false);

  // Use 'timeupdate' (fires when position advances) instead of 'playing',
  // which can fire optimistically for HLS before frames are actually rendering.
  const testStreamReady = router.query._test_stream_ready === 'true';
  const skipPreviewCookie =
    process.env.NODE_ENV !== 'production' && router.query._test_skip_preview_cookie === 'true';
  const [streamRendering, setStreamRendering] = useState(false);
  useEffect(() => {
    // Non-production: allow forcing stream ready via ?_test_stream_ready=true
    // for headless browser testing where HLS video doesn't autoplay.
    if (testStreamReady && process.env.NODE_ENV !== 'production') {
      setStreamRendering(true);
      return;
    }
    if (!livePlaying) return;
    const player = liveCamPlayerHandle.current?.getPlayer();
    if (!player) {
      // Fallback for tests where player ref isn't available.
      // Skip fallback during overlay flow — we need real playback detection.
      if (!isLineupCamOverlayEnabled) {
        setStreamRendering(true);
      }
      return;
    }
    const onTimeUpdate = () => setStreamRendering(true);
    player.on('timeupdate', onTimeUpdate);
    return () => {
      player.off('timeupdate', onTimeUpdate);
    };
  }, [livePlaying, liveCamPlayerHandle, testStreamReady, isLineupCamOverlayEnabled]);

  const {
    focusPolygon,
    overviewCameraId,
    isLoading: isLineupCamLogLoading,
  } = useLineupCamLog(camera.isLineupCam && isLineupCamOverlayEnabled ? camera._id : null);

  // End User Permissions Section

  // Start Spot Section

  const spotReportAssociated = useAppSelector(getSpotReportAssociated);
  const timezone = spotReportAssociated?.timezone ?? 'America/Los_Angeles';
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const supportsSmartRewinds = useCheckCameraSupportsSmartRewinds(camera, spot);
  const isFavorite = useSpotIsFavorite(spot?._id);

  const isPremiumCam = camera.isPremium;
  const camStillUrl = getCamStillImage(camera);
  const cameraWithoutHighlights = useMemo(() => {
    const { highlights: _highlights, ...restOfCamObj } = camera;
    return restOfCamObj;
  }, [camera]);

  // Use the overview camera's still as the overlay background
  const primaryCamStillUrl = useMemo(() => {
    const overviewCam = overviewCameraId
      ? spot?.cameras?.find((c) => c._id === overviewCameraId)
      : spot?.cameras?.find((c) => !c.isLineupCam);
    return overviewCam?.stillUrlFull ?? null;
  }, [spot?.cameras, overviewCameraId]);

  useEffect(() => {
    if (!overviewCameraId || !spot?.cameras) return;
    const found = spot.cameras.find((c) => c._id === overviewCameraId);
    if (!found) {
      reportErrorToNewRelic(
        new Error('LineupCamOverlay: overview camera not found in spot'),
        'lineupCamOverlayCameraMismatch',
        { overviewCameraId, spotId: spot._id, cameraCount: spot.cameras.length },
        true,
      );
    }
  }, [overviewCameraId, spot?.cameras, spot?._id]);

  const hasOverlayData = !!focusPolygon && !!primaryCamStillUrl;

  // Preload background images so they're cached before the overlay/paywall renders
  useEffect(() => {
    [primaryCamStillUrl, camera.stillUrlFull].forEach((url) => {
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [primaryCamStillUrl, camera.stillUrlFull]);

  const {
    isPreviewActive,
    isCountdownRunning,
    previewSecondsRemaining,
    previewChecked,
    overlayPhase,
    onExpandComplete,
  } = useLineupCamPreview({
    cameraId: camera._id,
    isLineupCam: !!camera.isLineupCam,
    hasLineupCamPermissions,
    isPaywallEnabled: isLineupCamPaywallEnabled,
    isPreviewEnabled: isLineupCamPreviewEnabled,
    isStreamReady: streamRendering,
    previewDurationSeconds,
    hasOverlayData,
    isOverlayDataLoading: isLineupCamOverlayEnabled && isLineupCamLogLoading,
    overlayHoldMs,
    skipPreviewCookie,
  });

  // Track streamRendering in a ref to avoid stale closures in the stall timer
  const streamRenderingRef = useRef(streamRendering);
  streamRenderingRef.current = streamRendering;

  // Detect stream stalls during preview: if no timeupdate for 2s, pause countdown
  useEffect(() => {
    if (!isPreviewActive || !isCountdownRunning) return;
    const player = liveCamPlayerHandle.current?.getPlayer();
    if (!player) return;

    let stallTimeout: NodeJS.Timeout | null = null;

    const resetStallTimer = () => {
      if (stallTimeout) clearTimeout(stallTimeout);
      if (!streamRenderingRef.current) setStreamRendering(true);
      stallTimeout = setTimeout(() => {
        setStreamRendering(false);
      }, 2000);
    };

    const onTimeUpdate = () => resetStallTimer();
    player.on('timeupdate', onTimeUpdate);
    resetStallTimer();

    return () => {
      player.off('timeupdate', onTimeUpdate);
      if (stallTimeout) clearTimeout(stallTimeout);
    };
  }, [isPreviewActive, isCountdownRunning, liveCamPlayerHandle]);

  // Scale the live player into the overlay cutout box, then expand to full size
  const livePlayerOverlayStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!focusPolygon || overlayPhase !== 'overlay') return undefined;
    const rect = computeFocusRect(focusPolygon);
    return {
      transformOrigin: '0 0',
      transform: `translate(${rect.left * 100}%, ${rect.top * 100}%) scale(${rect.right - rect.left}, ${rect.bottom - rect.top})`,
    };
  }, [focusPolygon, overlayPhase]);

  const livePlayerExpandingStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (overlayPhase !== 'expanding') return undefined;
    return {
      transformOrigin: '0 0',
      transform: 'translate(0, 0) scale(1, 1)',
      transition: `transform ${expandDurationMs}ms cubic-bezier(0.42, 0, 0.58, 1)`,
    };
  }, [overlayPhase, expandDurationMs]);

  // End Spot Section

  const {
    currentTimestampRef,
    handlePauseRewindPlayer,
    handlePlayRewindPlayer,
    isBuffering,
    isScrubbing,
    onRewindPlay,
    onRewindPlayerError,
    playbackSpeed,
    rewindCamPlayerHandle,
    rewindPlayerError,
    rewindPlaying,
    setCurrentTimestamp,
    setIsBuffering,
    setIsScrubbing,
    setIsVideoPlayerLoaded,
    setPlaybackSpeed,
    setPlayerTime,
    setSliderTimeValue,
    setUserWantsRewindPlaying,
    sliderTimeValueRef,
    userWantsRewindPlaying,
  } = useContext(RewindsCamPlayerWithScrubberControlContext);

  const currentTimestamp = useStateFromRefState(currentTimestampRef, 'camPlayerKbygRewinds');
  const sliderTimeValue = useStateFromRefState(sliderTimeValueRef, 'camPlayerKbygRewinds');

  const {
    rewindsPlaylists,
    currentPlaylist,
    updateCurrentPlaylist,
    error: rewindFetchError,
    reFetch,
    rewindsLoading,
    isNonPremiumPlaylist,
    rewindList,
    nextAvailableRewindFromLive,
    bucketStartTimeInMs,
    bucketEndTimeInMs,
  } = useContext(RewindsContext);

  // Player State Section

  const {
    currentPlayer,
    currentPlayerRef,
    currentPreviewPlayer,
    setCurrentPlayer,
    previewPlayerLive,
    previewPlayerRewind,
    setPlayerLive,
    setPlayerRewind,
  } = usePlayerState({
    adsEnabled: enabledFeatures.ads.video,
    liveCamPlayerHandle,
    rewindCamPlayerHandle,
    isNonPremiumPlaylist,
    hasCamRewindPermissions,
    setUserWantsRewindPlaying,
    multiCamViewActive,
    rewindsLoading,
    rewindsPlaylists,
  });

  // Force live player during lineup cam preview
  useEffect(() => {
    if (isPreviewActive) {
      setPlayerLive();
    }
  }, [isPreviewActive, setPlayerLive]);

  const hasHighlightsData = rewindsPlaylists && rewindsPlaylists.size > 0;

  const shouldShowFreeRewindsVideoPlayer =
    hasHighlightsData && !isUserEntitled && isCamHighlightsEnabled && !enabledFeatures.wavePool;

  const handlePlaylistChange = useCallback(
    (newPlaylistEndTime: string) => {
      setPlayerRewind();
      updateCurrentPlaylist(newPlaylistEndTime);
    },
    [updateCurrentPlaylist, setPlayerRewind],
  );

  const shouldRewindSwitchToLive = useMemo(() => {
    if (isNonPremiumPlaylist && !hasCamRewindPermissions) {
      return true;
    }
    if (currentPlaylist?.bucketEnd) {
      const now = new Date().getTime();
      const difference = now - currentPlaylist.bucketEnd.getTime();
      return difference < 1800000;
    }
    return false;
  }, [currentPlaylist?.bucketEnd, isNonPremiumPlaylist, hasCamRewindPermissions]);

  const onRewindBlockEnded = useCallback(() => {
    if (shouldRewindSwitchToLive) {
      setPlayerLive();
    }
  }, [setPlayerLive, shouldRewindSwitchToLive]);

  const [multiCamLivePlaying, setMultiCamLivePlaying] = useState<boolean>(false);
  const [hasPlayedLivePlayer, setHasPlayedLivePlayer] = useState(false);

  const onToggleLiveStreaming = useCallback((streaming: boolean) => {
    setLivePlaying(streaming);
    if (streaming) {
      setHasPlayedLivePlayer(true);
    }
  }, []);

  const handlePauseLivePlayer = useCallback(() => {
    liveCamPlayerHandle.current?.pause();
  }, [liveCamPlayerHandle]);

  const handlePlayLivePlayer = useCallback(() => {
    liveCamPlayerHandle.current?.play();
  }, [liveCamPlayerHandle]);

  const { togglePlayPause, pauseAllPlayers, playCurrentPlayer, pauseCurrentPlayer } =
    usePlayPauseControls({
      playing: currentPlayer === Player.REWIND ? rewindPlaying : livePlaying,
      pauseRewind: handlePauseRewindPlayer,
      playRewind: handlePlayRewindPlayer,
      pauseLive: handlePauseLivePlayer,
      playLive: handlePlayLivePlayer,
      currentPlayerRef,
    });

  useEffect(() => {
    if (!pageIsActive) {
      rewindCamPlayerHandle.current?.pause();
      liveCamPlayerHandle.current?.pause();
    } else if (currentPlayer === Player.LIVE) {
      liveCamPlayerHandle.current?.play();
    }
  }, [currentPlayer, pageIsActive, rewindCamPlayerHandle, liveCamPlayerHandle]);

  const { trackPlay } = useTrackCamPlayback(
    camera,
    'Kbyg Rewinds',
    spot?.name,
    spot?._id,
    isFavorite,
    'rewind',
    false,
    supportsSmartRewinds,
  );

  useEffect(() => {
    if (rewindPlaying && isNonPremiumPlaylist) {
      trackPlay();
    }
  }, [rewindPlaying, trackPlay, isNonPremiumPlaylist]);

  // End Player State Section

  // Ads Section

  const {
    isVideoAdPluginsError,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    videoAdPluginsLoaded,
    adSettings,
    adsRequired,
    camAdsReady,
    enableAdsFromLivePlayer,
    enableAdPlayer,
    setPlayAd,
    adRunning,
    setAdRunning,
    onPlayedDurationUpdate,
  } = useKbygRewindsPlayerAds({
    camera,
    spot,
    hasAdFreeCamPermissions,
    hasAdSupportedCamPermissions,
    isCamPaywallRedesignEnabled,
    enabledFeatures,
    multiCamViewActive,
    isNonPremiumPlaylist,
    pauseCurrentPlayer,
    playCurrentPlayer,
    playerPlaying: livePlaying || rewindPlaying || multiCamLivePlaying,
    isPreviewActive,
  });

  // End Ads Section

  // Paywall Section

  const showPremiumCamPaywall =
    !multiCamViewActive &&
    isPremiumCam &&
    !hasPremiumCamPermissions &&
    enabledFeatures.paywalls &&
    !isCamPaywallRedesignEnabled;

  const showRewindPaywall =
    !multiCamViewActive &&
    currentPreviewPlayer === Player.REWIND &&
    !hasCamRewindPermissions &&
    !isNonPremiumPlaylist &&
    enabledFeatures.paywalls &&
    !rewindsLoading;

  const isLineupCamWithPaywall =
    !!camera.isLineupCam && isLineupCamPaywallEnabled && !hasLineupCamPermissions;

  const showLivePaywall =
    !multiCamViewActive &&
    !hasAdSupportedCamPermissions &&
    !hasAdFreeCamPermissions &&
    enabledFeatures.paywalls &&
    // if they're allowed to watch ad free live cams then we don't show the paywall
    enabledFeatures.ads.video &&
    currentPreviewPlayer === Player.LIVE &&
    isCamPaywallRedesignEnabled &&
    !adRunning &&
    !isPreviewActive &&
    !isLineupCamWithPaywall;

  const isOverlayInProgress =
    overlayPhase === 'overlay' || overlayPhase === 'expanding' || overlayPhase === 'preview';

  const showLineupCamPaywall =
    !multiCamViewActive &&
    !!camera.isLineupCam &&
    !hasLineupCamPermissions &&
    isLineupCamPaywallEnabled &&
    enabledFeatures.paywalls &&
    !isPreviewActive &&
    !isOverlayInProgress &&
    !isLineupCamLogLoading &&
    // Wait for client-side cookie check before showing paywall (prevents flash during preview)
    (previewChecked || !isLineupCamPreviewEnabled);

  const showPaywallOverlay =
    showPremiumCamPaywall || showRewindPaywall || showLivePaywall || showLineupCamPaywall;

  const showGoLiveButton =
    !multiCamViewActive &&
    currentPreviewPlayer === Player.REWIND &&
    !hasCamRewindPermissions &&
    isCamPaywallRedesignEnabled;

  const showYesterdayPill =
    !multiCamViewActive &&
    currentPreviewPlayer === Player.REWIND &&
    !hasCamRewindPermissions &&
    isNonPremiumPlaylist &&
    enabledFeatures.paywalls &&
    !rewindsLoading &&
    !adRunning &&
    isCamPaywallRedesignEnabled;

  const multiCamPaywallActive =
    multiCamViewActive && !hasAdFreeCamPermissions && !hasAdSupportedCamPermissions;

  // End Paywall Section

  // Start Big Play Button Section

  const [hasClickedPlay, setHasClickedPlay] = useState(false);

  const handleBigPlayButtonClick = useCallback(() => {
    setHasClickedPlay(true);

    playCurrentPlayer();
  }, [playCurrentPlayer]);

  useEffect(() => {
    if (livePlaying || rewindPlaying || multiCamLivePlaying) {
      setHasClickedPlay(true);
    }
  }, [livePlaying, rewindPlaying, multiCamLivePlaying]);

  const shouldDisplayPlayOverlay =
    !hasClickedPlay &&
    !showPaywallOverlay &&
    !multiCamPaywallActive &&
    enableAdPlayer &&
    !rewindsLoading &&
    camAdsReady;

  const shouldAutoplay = isPreviewActive || !adsRequired || (hasClickedPlay && enableAdPlayer);

  // End Big Play Button Section

  // Start Player Props Section

  const showSingleCamPlayer =
    isPreviewActive || (!multiCamViewActive && !showPaywallOverlay && camAdsReady);

  const [liveCamReady, setLiveCamReady] = useState(false);

  const livePlayerProps = useMemo(
    () =>
      ({
        playerHandle: liveCamPlayerHandle,
        CamErrorComponent: CamError as unknown as ReactNode,
        adSettings: enableAdsFromLivePlayer ? adSettings : undefined,
        autoplay: shouldAutoplay
          ? AutoplayOptions.AUTOPLAY_ENABLED
          : AutoplayOptions.AUTOPLAY_DISABLED,
        camera: cameraWithoutHighlights,
        isPermitted: !enableAdsFromLivePlayer,
        latestRewind: false,
        location: isPreviewActive ? 'preview' : 'Kbyg Rewinds',
        playerId: `${camera._id}-kbyg-rewinds`,
        playerSkin: PlayerSkins.NONE,
        showOverlays: !isPreviewActive,
        showRewindLink: false,
        multiCamViewActive,
        onToggleStreaming: onToggleLiveStreaming,
        showInsightsSmartRewindsIcon: false,
        showPlayerOverlayControls: false,
        spotId: spot?._id,
        spotName: spot?.name,
        showLivePill: currentPreviewPlayer === Player.LIVE && !isPreviewActive,
        isInternalCam: camera.isInternalCam,
        isVideoAdPluginsError,
        setCamReady: setLiveCamReady,
        handleCamReady: true,
      }) as CamPlayerProps,
    [
      camera,
      cameraWithoutHighlights,
      currentPreviewPlayer,
      enableAdsFromLivePlayer,
      isVideoAdPluginsError,
      multiCamViewActive,
      onToggleLiveStreaming,
      spot,
      adSettings,
      shouldAutoplay,
      isPreviewActive,
    ],
  );

  const rewindPlayerProps = useMemo(
    () => ({
      ref: rewindCamPlayerHandle,
      rewinds: rewindList,
      beginning: currentPlaylist?.bucketStart || null,
      ending: currentPlaylist?.bucketEnd || null,
      playbackSpeed,
      camera,
      setCurrentTimestamp,
      setIsBuffering,
      onPlay: onRewindPlay,
      onRewindListEnded: onRewindBlockEnded,
      onPlayerError: onRewindPlayerError,
      setIsVideoPlayerLoaded,
      onPlayedDurationUpdate,
    }),
    [
      rewindCamPlayerHandle,
      rewindList,
      currentPlaylist?.bucketStart,
      currentPlaylist?.bucketEnd,
      playbackSpeed,
      camera,
      setCurrentTimestamp,
      setIsBuffering,
      onRewindPlay,
      onRewindBlockEnded,
      onRewindPlayerError,
      setIsVideoPlayerLoaded,
      onPlayedDurationUpdate,
    ],
  );

  // End Player Props Section

  // Start Segment Properties Section

  const clipButtonSegmentProperties = useMemo(() => {
    const indexOfCurrentBucketEndTimestamp =
      currentPlaylist && rewindsPlaylists
        ? Array.from(rewindsPlaylists.keys()).indexOf(currentPlaylist.bucketEnd.toISOString())
        : 0;

    return {
      camId: camera._id,
      camName: camera.alias,
      isCurrentClip: rewindsPlaylists
        ? indexOfCurrentBucketEndTimestamp === rewindsPlaylists.size - 1
        : false,
      isLiveSource: currentPreviewPlayer === Player.LIVE,
      lineupCam: !!camera.isLineupCam,
      premiumCam: camera.isPremium,
      clipStartTime: currentPlaylist?.bucketStart ?? null,
      smartCam: supportsSmartRewinds,
      spotId: spot?._id,
      spotName: spot?.name,
      subregionId: spot?.subregion?._id,
    };
  }, [
    currentPlaylist,
    rewindsPlaylists,
    camera._id,
    camera.alias,
    camera.isLineupCam,
    camera.isPremium,
    currentPreviewPlayer,
    supportsSmartRewinds,
    spot?._id,
    spot?.name,
    spot?.subregion?._id,
  ]);

  const segmentPropsRewindCTA = useMemo(
    () => ({
      location: 'cam rewind scrubber',
      category: 'cams & reports',
      pageName: 'Spot Report',
      spotId: spot?._id,
      spotName: spot?.name,
    }),
    [spot],
  );

  // End Segment Properties Section

  // Start Stills Section

  const { currentPreviewImage, showPreviewImage } = useStills({
    disableStills: !hasCamRewindPermissions && !isNonPremiumPlaylist,
    currentPlaylist,
    isBuffering,
    isScrubbing,
    sliderTimeValue,
  });

  // End Stills Section

  // Start Clips Button Section

  const { showClipsPrompt, clipButtonDisabled, clipsButtonUrl } = useClipsButton({
    camera,
    spot,
    currentPreviewPlayer,
    currentTimestamp,
    nextAvailableRewindFromLive,
    bucketEndTimeInMs,
    hasCamRewindPermissions,
    rewindPlayerError,
    enabledFeatures,
    adRunning,
    isNative,
  });

  // End Clips Button Section

  // Start Picture In Picture Section

  const isPictureInPictureEnabled = window?.document?.pictureInPictureEnabled;

  const togglePictureInPicture = useCallback(() => {
    const togglePlayer =
      currentPlayer === Player.LIVE ? liveCamPlayerHandle : rewindCamPlayerHandle;
    if (togglePlayer.current?.requestNativePictureInPicture) {
      togglePlayer.current?.requestNativePictureInPicture();
    }
  }, [currentPlayer, rewindCamPlayerHandle, liveCamPlayerHandle]);

  useEffect(() => {
    if (camera._id) {
      rewindCamPlayerHandle.current?.exitNativePictureInPicture();
    }
  }, [camera._id, rewindCamPlayerHandle, setCurrentPlayer]);

  // End Picture In Picture Section

  // Start Fullscreen Section

  const { fullscreenElementRef, isFullscreen, supportsFullscreen, handleRequestFullscreen } =
    useKbygRewindsFullscreen({
      width,
      height,
      multiCamViewActive,
      currentPreviewPlayer,
      liveCamPlayerHandle,
      rewindCamPlayerHandle,
    });

  // End Fullscreen Section

  // Start Player Controls Section

  const showFullScreenControl =
    (currentPreviewPlayer === Player.REWIND ||
      (currentPreviewPlayer === Player.LIVE && hasPlayedLivePlayer) ||
      showLivePaywall) &&
    !multiCamViewActive;

  const showAllControls =
    !adRunning && !showPaywallOverlay && showSingleCamPlayer && !shouldDisplayPlayOverlay;

  const showPictureInPictureControl =
    hasAdFreeCamPermissions && isMinWidthTablet && isPictureInPictureEnabled && showAllControls;

  const showPlaybackSpeedControl =
    !multiCamViewActive && currentPreviewPlayer === Player.REWIND && showAllControls;

  const showFeedbackModal =
    isFeedbackModalEnabled && !isFullscreen && showAllControls && !adRunning;

  const showShareModal = isWebSharingEnabled && !isFullscreen && showAllControls && !adRunning;

  const showRewindsScrubber = !isResizing && !rewindsLoading && !adRunning;

  const highlightsCamFeatures = useMemo(() => enabledFeatures, [enabledFeatures]);

  // End Player Controls Section

  const formattedRewindDisplayDay = useMemo(() => {
    if (showYesterdayPill && currentPlaylist?.bucketStart) {
      const currentPlaylistDay = formatInTimeZone(currentPlaylist.bucketStart, timezone, 'EEE');
      return getDayString(currentPlaylistDay, timezone, 'EEE');
    }
    return '';
  }, [currentPlaylist, showYesterdayPill, timezone]);

  if (shouldShowFreeRewindsVideoPlayer && !showLineupCamPaywall && !isLineupCamWithPaywall) {
    return (
      <CamHighlightsPlayer
        camera={camera}
        enabledFeatures={highlightsCamFeatures}
        highlights={rewindsPlaylists}
        rewindsFetching={rewindsLoading}
        rewindFetchError={rewindFetchError}
        spot={spot}
      />
    );
  }

  return (
    <>
      <VideoJsAdScripts
        hasAdFreeCamPermissions={hasAdFreeCamPermissions}
        onVideoJsGoogleImaLoaded={onVideoJsGoogleImaLoaded}
        onVideoJsGoogleImaError={onVideoJsGoogleImaError}
      />
      <div
        className={classNames({
          [styles.fullscreenWrapper]: !isTouchDevice && isFullscreen,
          [styles.touchFullscreen]: isTouchDevice && isFullscreen,
        })}
        ref={fullscreenElementRef}
      >
        <div
          className={classNames({
            [styles.wrapper]: true,
            [styles.wrapperWithoutInsights]: !supportsSmartRewinds,
          })}
          ref={camPlayerKbygRewindsElement}
        >
          <div
            className={classNames([
              styles.playerWrapper,
              multiCamViewActive && styles.multicamViewActive,
              !currentPlaylist?.hasInsights && styles.playerWrapperWithoutInsights,
              (showPremiumCamPaywall ||
                showRewindPaywall ||
                showLineupCamPaywall ||
                (showLivePaywall && !isNonPremiumPlaylist)) &&
                styles.playerWithPaywall,
            ])}
            data-testid="kbyg-rewinds-player-wrapper"
          >
            {(showRewindPaywall || showPremiumCamPaywall) && (
              <PremiumImageOverlayCTA
                card={showPremiumCamPaywall ? premiumCamPaywallCard : rewindPaywallCard}
                cssModules={premiumOverlayCTACssModules}
                hostAd={<CamHostAd camera={camera} spot={spot} />}
                imageAlt="Recent conditions"
                imageUrl={camStillUrl}
                segmentProperties={segmentPropsRewindCTA}
                spotName={spot.name}
              />
            )}
            {showLivePaywall && (
              <LiveCamPaywall backgroundImage={camera.stillUrlFull} spot={spot} />
            )}
            {enableAdPlayer && !isLineupCamWithPaywall && (
              <div
                className={classNames({
                  [styles.hidden]: !adRunning,
                  [styles.camAdPlayer]: true,
                })}
                data-testid={adRunning ? 'ad-player' : undefined}
              >
                <CamAdPlayer
                  adSettings={adSettings}
                  setPlayAd={setPlayAd}
                  hasAdFreeCamPermissions={hasAdFreeCamPermissions}
                  setAdRunning={setAdRunning}
                  videoAdPluginsLoaded={videoAdPluginsLoaded}
                  spot={spot}
                  segmentScreen="spot"
                />
              </div>
            )}
            <PlayButtonOverlay
              visible={shouldDisplayPlayOverlay}
              onClick={handleBigPlayButtonClick}
            />
            {!multiCamViewActive && currentPreviewPlayer === Player.REWIND && (
              <div className={classNames([styles.clipsPrompt, showClipsPrompt && styles.show])}>
                <SmartClipsOverlayPrompt
                  supportsSmartRewinds={supportsSmartRewinds}
                  clipsButtonUrl={clipsButtonUrl}
                  clipButtonSegmentProperties={clipButtonSegmentProperties}
                  disabled={clipButtonDisabled}
                />
              </div>
            )}
            {showYesterdayPill && (
              <YesterdayPill spot={spot} formattedRewindDisplayDay={formattedRewindDisplayDay} />
            )}
            {isPreviewActive && !isCountdownRunning && !isOverlayInProgress && (
              <div className={styles.previewLoadingOverlay}>
                <LoadingSpinner />
              </div>
            )}
            {(overlayPhase === 'overlay' || overlayPhase === 'expanding') &&
              focusPolygon &&
              primaryCamStillUrl && (
                <LineupCamOverlay
                  backgroundStillUrl={primaryCamStillUrl}
                  focusPolygon={focusPolygon}
                  overlayPhase={overlayPhase}
                  onExpandComplete={onExpandComplete}
                  expandDurationMs={expandDurationMs}
                />
              )}
            {isPreviewActive && isCountdownRunning && (
              <PreviewCountdownPill secondsRemaining={previewSecondsRemaining} />
            )}
            <div className={styles.camTopControlsRight}>
              {showPlaybackSpeedControl && (
                <PlaybackSpeedControl
                  isDisabled={false}
                  playbackSpeed={playbackSpeed}
                  setPlaybackSpeed={setPlaybackSpeed}
                />
              )}
              {showFeedbackModal && (
                <CamFeedbackModal
                  camera={camera}
                  spot={spot}
                  currentPlayer={currentPlayer}
                  currentTimestampRef={currentTimestampRef}
                  currentPlaylist={currentPlaylist}
                  timezone={timezone}
                  isWavePool={enabledFeatures.wavePool}
                />
              )}
              {showShareModal && (
                <ShareModal
                  spotName={spot.name}
                  spotId={spot._id}
                  cameraId={camera._id}
                  cameraTitle={camera.title}
                  onOpenModal={pauseAllPlayers}
                  onCloseModal={playCurrentPlayer}
                  currentTimestampRef={currentTimestampRef}
                  spotTimezone={timezone}
                  isRewindPlayer={currentPreviewPlayer === Player.REWIND}
                />
              )}
              {showPictureInPictureControl && (
                <IconButton
                  onClick={togglePictureInPicture}
                  className={styles.pipButton}
                  disabled={rewindsLoading || !!rewindPlayerError || !liveCamReady}
                >
                  <PictureInPictureIcon />
                </IconButton>
              )}
              {showFullScreenControl && (
                <FullscreenControl
                  isFullscreen={isFullscreen}
                  toggleFullscreen={handleRequestFullscreen}
                  supportsFullscreen={supportsFullscreen}
                />
              )}
            </div>
            <div
              className={classNames({
                [styles.hidden]: !showSingleCamPlayer,
              })}
            >
              {currentPlayer === Player.REWIND && rewindPlayerError && (
                <div
                  className={styles.rewindNoVideoErrorWrapper}
                  data-testid="kbyg-rewinds-player-error"
                >
                  <CamError
                    messageData={{
                      message: rewindPlayerError.message,
                      subMessage: rewindPlayerError.subMessage || '',
                    }}
                  />
                </div>
              )}
              {showPreviewImage && (
                <CamPlayerPreviewImage storyboards={currentPreviewImage?.storyboards ?? []} />
              )}
              <div
                className={classNames(styles.videoPlayer, {
                  [styles.hidden]: currentPreviewPlayer === Player.LIVE || isPreviewActive,
                })}
                data-testid="kbyg-rewinds-rewind-player-wrapper"
                ref={playerEventTriggerRef}
              >
                <RewindCamPlayer {...rewindPlayerProps} />
                {isBuffering &&
                  rewindPlaying &&
                  currentPlayer === Player.REWIND &&
                  currentPreviewImage && <LoadingSpinner />}
              </div>
              {(hasAdFreeCamPermissions ||
                hasAdSupportedCamPermissions ||
                !enabledFeatures.ads.video ||
                enableAdsFromLivePlayer ||
                isPreviewActive) && (
                <div
                  className={classNames(styles.livePlayerWrapper, {
                    [styles.hidden]: currentPreviewPlayer !== Player.LIVE && !isPreviewActive,
                  })}
                  style={livePlayerOverlayStyle ?? livePlayerExpandingStyle}
                  data-testid="kbyg-rewinds-live-player-wrapper"
                >
                  <CamPlayerV2 {...livePlayerProps} key={`camera-id-${camera._id}`} />
                </div>
              )}
            </div>
            {multiCamViewActive && (
              <CamPlayerKbygRewindsMultiCam
                disablePaywalls={enabledFeatures.paywalls === false}
                multiCamViewActive={multiCamViewActive}
                numCamsDisplayed={numCamsDisplayed}
                onToggleStreaming={setMultiCamLivePlaying}
                primaryCam={camera}
                sortedCameras={sortedCameras}
                autoplay={shouldAutoplay}
              />
            )}
          </div>
          {showLineupCamPaywall && (
            <LineupCamPaywall backgroundImage={camera.stillUrlFull} spot={spot} />
          )}
          <div
            className={classNames([
              styles.playerControls,
              !currentPlaylist?.hasInsights && styles.playerControlsWithoutInsights,
            ])}
          >
            <div className={classNames(styles.rewindControlsContainer)}>
              {showRewindsScrubber && (
                <CamPlayerRewindsScrubber
                  bucketEndTimeInMs={bucketEndTimeInMs || 1}
                  bucketStartTimeInMs={bucketStartTimeInMs || 0}
                  camera={camera}
                  currentPlayer={currentPlayer}
                  currentPreviewPlayer={currentPreviewPlayer}
                  errors={currentPlaylist?.errors || []}
                  hasInsights={currentPlaylist?.hasInsights || false}
                  hasRewinds={(rewindsPlaylists?.size || 0) > 0}
                  multiCamViewActive={multiCamViewActive}
                  onScrub={setIsScrubbing}
                  playerTime={currentTimestamp?.getTime()}
                  playerWidth={playerWidth}
                  setPlayerTime={setPlayerTime}
                  setSliderTimeValue={setSliderTimeValue}
                  showCamHostInRewind={showGoLiveButton}
                  isPreviewActive={isPreviewActive}
                  showLive={setPlayerLive}
                  showPreviewOfLive={previewPlayerLive}
                  showPreviewOfRewind={previewPlayerRewind}
                  showRewind={setPlayerRewind}
                  showRewindGraph
                  sliderTimeValue={sliderTimeValue}
                  spot={spot}
                  supportsSmartRewinds={supportsSmartRewinds && hasGraphRewindPermissions}
                  timezone={timezone}
                  updateCurrentPlaylist={handlePlaylistChange}
                  userWantsRewindPlaying={userWantsRewindPlaying}
                  waveGraphBars={currentPlaylist?.waveGraphBars || []}
                  waveUnits={currentPlaylist?.associated?.units.waves.height || 'ft'}
                  playing={currentPlayer === Player.REWIND ? rewindPlaying : livePlaying}
                  currentTimestampRef={currentTimestampRef}
                  hasCurrentTimestamp={!!currentTimestamp}
                  playerRef={camPlayerKbygRewindsElement.current}
                  isFullscreen={isFullscreen}
                  hasCamRewindPermissions={hasCamRewindPermissions || isNonPremiumPlaylist}
                  scrubberDayLabel={formattedRewindDisplayDay}
                  paywallActive={showPaywallOverlay}
                  onTogglePlayPause={togglePlayPause}
                />
              )}
              {(rewindFetchError || !rewindsPlaylists?.size) &&
                !rewindsLoading &&
                !multiCamViewActive &&
                hasCamRewindPermissions && (
                  <Box
                    className={styles.rewindFetchErrorContainer}
                    data-testid="rewind-fetch-error"
                  >
                    <Typography variant="caption1" className={styles.rewindFetchErrorMessage}>
                      {rewindFetchError ? 'Rewinds failed to load.' : 'No rewinds available.'}
                    </Typography>
                    {rewindFetchError && (
                      <Button
                        data-testid="rewind-fetch-error-retry"
                        className={styles.rewindFetchRetryButton}
                        size="small"
                        color="secondary"
                        variant="text"
                        disableTouchRipple
                        onClick={
                          rewindsLoading
                            ? undefined
                            : () => {
                                reFetch();
                              }
                        }
                        style={{ cursor: rewindsLoading ? 'auto' : 'pointer' }}
                      >
                        <Typography variant="caption1" className={styles.rewindFetchErrorMessage}>
                          Retry
                        </Typography>
                      </Button>
                    )}
                  </Box>
                )}
              {(rewindsLoading || (isResizing && !!rewindsPlaylists?.size && !rewindFetchError)) &&
                supportsSmartRewinds && (
                  <Skeleton
                    animation="pulse"
                    className={classNames(styles.loading, {
                      [styles.loadingSmartRewinds]: supportsSmartRewinds && !multiCamViewActive,
                    })}
                    variant="rounded"
                  />
                )}
              <div className={classNames(isPreviewActive && styles.previewDisabled)}>
                <CamRewindsControlBar
                  disablePaywalls={enabledFeatures.paywalls === false}
                  clipButtonDisabled={clipButtonDisabled}
                  clipButtonSegmentProperties={clipButtonSegmentProperties}
                  clipButtonUrl={clipsButtonUrl}
                  currentPlaylist={currentPlaylist}
                  isFullscreen={isFullscreen}
                  isLive={currentPreviewPlayer === Player.LIVE}
                  isMultiCam={isMultiCam}
                  multiCamViewActive={multiCamViewActive}
                  rewindsLoading={rewindsLoading}
                  rewindsPlaylists={rewindsPlaylists}
                  setActiveMenu={setActiveMenu}
                  setMultiCamViewActive={setMultiCamViewActive}
                  isWavePool={enabledFeatures.wavePool}
                  showLiveButton={showGoLiveButton}
                  spot={spot}
                  supportsSmartRewinds={supportsSmartRewinds}
                  updateCurrentPlaylist={handlePlaylistChange}
                  adRunning={adRunning}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CamPlayerKbygRewinds;
